<template>
    <v-container fluid>
        <v-data-table
            :headers="headers"
            :items="processesFiltered"
            class="elevation-0"
            :mobile-breakpoint="0"
            :loading="loading"
            disable-pagination
            hide-default-footer
        >
            <template v-slot:top>
                <v-row
                    no-gutter
                    class="mt-0 mx-0 pt-4 pb-0 pb-4"
                    :style="{ 'background-color': '#eeeeee' }"
                >
                    <v-col cols="12" class="d-flex mb-n3">
                        <h1 class="mr-4">PROCESSES</h1>
                        <v-btn
                            v-if="editSettingsPermission && !loading"
                            rounded
                            color="primary"
                            @click="openCreateProcess"
                            class="mx-2"
                        >
                            <v-icon>
                                mdi-plus
                            </v-icon>
                            {{ $vuetify.breakpoint.smAndDown ? '' : 'NEW' }}
                        </v-btn>
                    </v-col>
                </v-row>
            </template>
            <!--HEADERS-->
            <template v-slot:[`header.name`]="{ header }">
                <v-text-field
                    class="pb-2 pt-3 mt-2"
                    :label="header.text"
                    v-model="searchByName"
                    dense
                    prepend-icon="mdi-filter"
                />
            </template>
            <template v-slot:[`header.minimumCapacityInMonth`]>
                MINIMUM CAPACITY IN {{ currentMonth }}
            </template>
            <template v-slot:[`header.workDays`]>
                WORK DAYS IN {{ currentMonth }}
            </template>
            <template v-slot:[`header.laborValue`]>
                LABOR VALUE IN {{ currentMonth }}
            </template>
            <!--ITEMS-->
            <template v-slot:[`item.assemblyProcess`]="{ item }">
                <div class="d-flex justify-center">
                    <v-icon
                        :color="item.assemblyProcess ? 'primary' : 'error'"
                        >{{
                            item.assemblyProcess ? 'mdi-check' : 'mdi-close'
                        }}</v-icon
                    >
                </div>
            </template>
            <!-- Minimum Capacity -->
            <template v-slot:[`item.minimumCapacityInMonth`]="{ item }">
                <div class="d-flex justify-center">
                    <p>
                        {{
                            item.minimumCapacityInMonth
                                ? item.minimumCapacityInMonth[currentMonth] ||
                                  Object.values(item.minimumCapacityInMonth).at(
                                      -1
                                  )
                                : 0
                        }}
                        Kg
                    </p>
                </div>
            </template>
            <!-- Work Days Capacity -->
            <template v-slot:[`item.workDays`]="{ item }">
                <div class="d-flex justify-center">
                    <p>
                        {{
                            item.workDays
                                ? item.workDays[currentMonth] ||
                                  Object.values(item.workDays).at(-1)
                                : 0
                        }}
                    </p>
                </div>
            </template>
            <!-- Minimum capacity per day -->
            <template v-slot:[`item.minimumCapacityPerDay`]="{ item }">
                <div class="d-flex justify-center">
                    <p>
                        {{ totalMonthlyCapacity(item) }}
                    </p>
                </div>
            </template>
            <!-- Labor Value -->
            <template v-slot:[`item.laborValue`]="{ item }">
                <div class="d-flex justify-center">
                    <p>
                        {{
                            item.laborValue
                                ? item.laborValue[currentMonth] ||
                                  Object.values(item.laborValue).at(-1)
                                : 0
                        }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.coordinators`]="{ item }">
                <div class="px-2 coordinators-cell">
                    <span
                        v-if="!coordinatorLabelList(item).length"
                        class="grey--text"
                    >
                        —
                    </span>
                    <span v-else>
                        {{ coordinatorLabelList(item).join(', ') }}
                    </span>
                </div>
            </template>
            <template v-slot:[`item.enabled`]="{ item }">
                <div class="d-flex justify-center">
                    <v-switch
                        :input-value="item.enabled !== false"
                        color="primary"
                        dense
                        hide-details
                        class="mt-0 pt-0"
                        :disabled="!editSettingsPermission || loading"
                        @change="toggleProcessEnabled(item)"
                    />
                </div>
            </template>
            <template v-slot:[`item.actions`]="{ item }">
                <div class="d-flex justify-center">
                    <v-btn
                        small
                        icon
                        @click="openEditProcess(item)"
                        :disabled="!editSettingsPermission || loading"
                    >
                        <v-icon>mdi-pencil</v-icon>
                    </v-btn>
                    <v-btn
                        small
                        icon
                        @click="openDeleteProcess(item)"
                        :disabled="!editSettingsPermission || loading"
                    >
                        <v-icon>mdi-delete</v-icon>
                    </v-btn>
                    <v-btn
                        small
                        icon
                        @click="openLog(item)"
                        :disabled="loading"
                    >
                        <v-icon>mdi-history</v-icon>
                    </v-btn>
                </div>
            </template>
        </v-data-table>
        <!--PROCESSES FORM-->
        <v-dialog
            v-model="openProcessForm"
            :retain-focus="false"
            persistent
            width="640px"
        >
            <ProcessesForm
                v-if="openProcessForm"
                :createForm="createForm"
                :process="selectedProcess"
                :originalProcess="originalProcess"
                :processSettings="setting"
                :liteUsers="liteUsers"
                @addProcess="addProcess"
                @replaceProcess="replaceProcess"
                @close="closeEditProcess"
            />
        </v-dialog>
        <!--Delete Process-->
        <v-dialog
            v-model="openDeleteWarning"
            persistent
            max-width="400px"
            :retain-focus="false"
        >
            <v-card :loading="loading" v-if="openDeleteWarning">
                <v-card-title class="text-h5">Delete Process</v-card-title>
                <v-card-text>
                    Are you sure you want to delete this process?
                </v-card-text>
                <v-card-actions>
                    <v-btn text color="secondary" @click="closeDeleteProcess">
                        Close
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn
                        @click="deleteProcess"
                        text
                        color="error"
                        :loading="loading"
                    >
                        Delete
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <!-- Process Log -->
        <v-dialog
            v-model="logDialog"
            :retain-focus="false"
            persistent
            width="800px"
        >
            <ProcessesDataLog
                v-if="logDialog"
                :process="selectedProcess"
                @closeDialog="closeLog"
            />
        </v-dialog>
    </v-container>
</template>

<script>
import { mapMutations } from 'vuex'
import API from '@/services/api'
import _ from 'lodash'
import moment from 'moment'

export default {
    name: 'Processes',
    props: {
        setting: {
            type: Object,
            default: () => ({}),
        },
    },
    components: {
        ProcessesForm: () => import('@/components/Processes/ProcessesForm.vue'),
        ProcessesDataLog: () =>
            import('@/components/Processes/ProcessesDataLog.vue'),
    },
    data: () => ({
        loading: false,
        currentMonth: {},
        processes: [],
        selectedProcess: {},
        originalProcess: {},
        createForm: true,
        openProcessForm: false,
        openDeleteWarning: false,
        processSettings: {},
        headers: [
            {
                text: 'NAME',
                value: 'name',
                sortable: false,
                width: '200',
            },
            {
                text: 'DESCRIPTION',
                value: 'description',
                sortable: false,
            },
            {
                text: 'ASSEMBLY PROCESS',
                value: 'assemblyProcess',
                align: 'center',
                sortable: false,
            },
            {
                text: `MINIMUM CAPACITY`,
                value: 'minimumCapacityInMonth',
                align: 'center',
                sortable: false,
            },
            {
                text: `WORK DAYS`,
                value: 'workDays',
                align: 'center',
                sortable: false,
            },
            {
                text: `MINIMUM CAPACITY PER DAY`,
                value: 'minimumCapacityPerDay',
                align: 'center',
                sortable: false,
            },
            {
                text: `LABOR VALUE`,
                value: 'laborValue',
                align: 'center',
                sortable: false,
            },
            {
                text: 'COORDINATORS',
                value: 'coordinators',
                sortable: false,
                width: '220',
            },
            {
                text: 'ENABLED',
                value: 'enabled',
                align: 'center',
                sortable: false,
                width: '100',
            },
            {
                text: 'ACTIONS',
                value: 'actions',
                sortable: false,
                align: 'center',
            },
        ],
        searchByName: undefined,
        editSettingsPermission: false,
        logDialog: false,
        liteUsers: [],
    }),
    async mounted() {
        try {
            this.loading = true
            // get current month
            this.currentMonth = moment(new Date()).format('YYYY-MM')
            const [processes, liteRes] = await Promise.all([
                API.getProcesses(this.setting.id),
                API.getLiteUsers(),
            ])
            this.processes = processes
            this.liteUsers = liteRes.data.users || []
            // get auth user
            const {
                data: { user },
            } = await API.getMyInfo()
            // Set edit settings permission
            this.editSettingsPermission = user.permissions.includes(
                'editSettings'
            )
        } catch (error) {
            this.setErrorItems({
                name: this.$options.name,
                message: error.message,
            })
        } finally {
            this.loading = false
        }
    },
    computed: {
        liteUserNameById() {
            return this.liteUsers.reduce((acc, u) => {
                acc[u.id] = u.name || u.email || u.id
                return acc
            }, {})
        },
        processesFiltered() {
            const conditions = []
            if (this.searchByName) {
                conditions.push(this.filterByName)
            }
            if (conditions.length > 0) {
                return this.processes.filter(process => {
                    return conditions.every(condition => {
                        return condition(process)
                    })
                })
            }
            return this.processes
        },
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        coordinatorLabelList(item) {
            const ids = item.coordinators
            if (!Array.isArray(ids) || !ids.length) {
                return []
            }
            return ids.map(id => this.liteUserNameById[id] || id)
        },
        filterByName(item) {
            return item.name
                .toLowerCase()
                .includes(this.searchByName.toLowerCase())
        },
        replaceProcess(processToUpdate) {
            const index = this.processes.findIndex(
                process => process.id == processToUpdate.id
            )
            if (index >= 0) {
                this.processes.splice(index, 1, {
                    ...this.processes[index],
                    ...processToUpdate,
                })
            }
        },
        addProcess(process) {
            this.processes.push(process)
            this.searchByName = ''
        },
        removeProcess() {
            const index = this.processes.findIndex(
                process => process.id == this.selectedProcess.id
            )
            if (index >= 0) {
                this.processes.splice(index, 1)
            }
        },
        async deleteProcess() {
            try {
                this.loading = true
                await API.deleteProcess({
                    settingId: this.setting.id,
                    processId: this.selectedProcess.id,
                })
                this.removeProcess()
                this.closeDeleteProcess()
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        closeDeleteProcess() {
            this.selectedProcess = {}
            this.openDeleteWarning = false
        },
        openDeleteProcess(item) {
            this.selectedProcess = _.cloneDeep(item)
            this.openDeleteWarning = true
        },
        openLog(item) {
            this.selectedProcess = _.cloneDeep(item)
            this.logDialog = true
        },
        closeLog(item) {
            this.selectedProcess = _.cloneDeep(item)
            this.logDialog = false
        },
        closeEditProcess() {
            this.createForm = true
            this.selectedProcess = {}
            this.originalProcess = {}
            this.openProcessForm = false
        },
        openEditProcess(item) {
            this.createForm = false
            const proc = _.cloneDeep(item)
            if (!Array.isArray(proc.coordinators)) {
                proc.coordinators = []
            }
            this.selectedProcess = proc
            this.originalProcess = _.cloneDeep(proc)
            this.openProcessForm = true
        },
        openCreateProcess() {
            this.createForm = true
            this.selectedProcess = { coordinators: [] }
            this.originalProcess = {}
            this.openProcessForm = true
        },
        async toggleProcessEnabled(item) {
            try {
                this.loading = true
                const minCap =
                    item.minimumCapacityInMonth?.[this.currentMonth] ??
                    Object.values(item.minimumCapacityInMonth || {}).at(-1) ??
                    0
                const workDays =
                    item.workDays?.[this.currentMonth] ??
                    Object.values(item.workDays || {}).at(-1) ??
                    0
                const laborValue =
                    item.laborValue?.[this.currentMonth] ??
                    Object.values(item.laborValue || {}).at(-1) ??
                    0
                const updated = await API.updateProcess({
                    settingId: this.setting.id,
                    processId: item.id,
                    minimumCapacityInMonth: minCap,
                    workDays,
                    laborValue,
                    enabled: item.enabled === false,
                })
                this.replaceProcess(updated)
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        totalMonthlyCapacity(item) {
            const minCap =
                item.minimumCapacityInMonth?.[this.currentMonth] ??
                Object.values(item.minimumCapacityInMonth || {}).at(-1) ??
                0

            const workDays =
                item.workDays?.[this.currentMonth] ??
                Object.values(item.workDays || {}).at(-1) ??
                0

            return workDays > 0 ? minCap / workDays : 0
        },
    },
}
</script>

<style>
.v-data-table {
    border: 1px solid #eeeeee;
}
thead {
    background: #eeeeee;
}
.v-input .v-label {
    font-size: 12px;
}
</style>
